import { PaymentMethod, usePermissions } from '@tyro/api';
import type { ReturnTypeFromUseContactsForSelect } from '@tyro/people';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  type ReturnTypeFromUseCustomerFeesInfo,
  useCustomerFeesInfo,
} from '../../../api/customer-info';
import type { ReturnTypeFromUseStudentFees } from '../../../api/student-fees';

export type PaymentsToPayAndMethod = {
  paymentMethod: PaymentMethod;
  cardPaymentType: 'one-time' | 'payment-plan';
  fees: (ReturnTypeFromUseStudentFees & { amountToPay: number })[];
  total: number;
  onBehalfOf?: ReturnTypeFromUseContactsForSelect;
  details?: string;
};

export type PayFeesSettingsContextValue = {
  onClose: () => void;
  step: number;
  nextStep: () => void;
  previousStep: () => void;
  nextAction: () => unknown;
  setNextAction: Dispatch<SetStateAction<() => unknown>>;
  paymentsToPayAndMethod: PaymentsToPayAndMethod | undefined;
  setPaymentsToPayAndMethod: Dispatch<
    SetStateAction<PaymentsToPayAndMethod | undefined>
  >;
  disableConfirm: boolean;
  setDisableConfirm: Dispatch<SetStateAction<boolean>>;
  isSubmitting: boolean;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  customerFeesInfo: ReturnTypeFromUseCustomerFeesInfo | undefined;
  isCustomerInfoLoading: boolean;
};

const PayFeesSettingsContext = createContext<
  PayFeesSettingsContextValue | undefined
>(undefined);

export function PayFeesSettingsProvider({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode | ((value: PayFeesSettingsContextValue) => ReactNode);
}) {
  const [step, setStep] = useState(0);
  const [disableConfirm, setDisableConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextAction, setNextAction] = useState<() => void>(() => {});
  const [paymentsToPayAndMethod, setPaymentsToPayAndMethod] = useState<
    PaymentsToPayAndMethod | undefined
  >();
  const { isStaffUser } = usePermissions();
  const onBehalfOfEmail =
    paymentsToPayAndMethod?.onBehalfOf?.personalInformation?.primaryEmail
      ?.email;
  const isCardPayment =
    paymentsToPayAndMethod?.paymentMethod === PaymentMethod.Card;

  const { data: customerFeesInfo, isLoading: isCustomerInfoLoading } =
    useCustomerFeesInfo(
      {
        onBehalfOf: onBehalfOfEmail,
        partyId: paymentsToPayAndMethod?.onBehalfOf?.partyId,
      },
      {
        enabled: open && isCardPayment && (!isStaffUser || !!onBehalfOfEmail),
      },
    );

  const nextStep = () => {
    setStep((previousValue) => previousValue + 1);
  };

  const previousStep = () => {
    setStep((previousValue) => previousValue - 1);
  };

  const value = useMemo(
    () => ({
      onClose,
      step,
      nextStep,
      previousStep,
      nextAction,
      setNextAction,
      setPaymentsToPayAndMethod,
      paymentsToPayAndMethod,
      disableConfirm,
      setDisableConfirm,
      isSubmitting,
      setIsSubmitting,
      customerFeesInfo,
      isCustomerInfoLoading,
    }),
    [
      onClose,
      step,
      nextStep,
      previousStep,
      nextAction,
      setNextAction,
      setPaymentsToPayAndMethod,
      paymentsToPayAndMethod,
      disableConfirm,
      setDisableConfirm,
      isSubmitting,
      setIsSubmitting,
      customerFeesInfo,
      isCustomerInfoLoading,
    ],
  );

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(0);
        setPaymentsToPayAndMethod(undefined);
        setNextAction(() => {});
      }, 500);
    }
  }, [open]);

  return (
    <PayFeesSettingsContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </PayFeesSettingsContext.Provider>
  );
}

export function usePayFeesSettings() {
  const context = useContext(PayFeesSettingsContext);
  if (context === undefined) {
    throw new Error('usePayFeesSettings must be used within a PayFeesContext');
  }
  return context;
}
